/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Pak Kwan 2026-09-07: Split-screen constants for Phase 4
 */

// Split-screen configuration interface
export interface SplitConfig {
  /** Whether split mode is active */
  enabled: boolean;
  /** Left section width as percentage (10-60) */
  leftWidth: number;
  /** Middle section width as percentage (20-60) */
  middleWidth: number;
  /** ID of Frame app to display in left section */
  leftFrameId: string | null;
  /** ID of Frame app to display in right section */
  rightFrameId: string | null;
}

// Preset configuration type
export interface SplitPreset {
  name: string;
  label: string;
  leftWidth: number;
  middleWidth: number;
  description: string;
}

// Predefined presets
export const SPLIT_PRESETS: Record<string, SplitPreset> = {
  equal: {
    name: 'equal',
    label: 'Equal (33-33-33)',
    leftWidth: 33,
    middleWidth: 34,
    description: 'Balanced three-way split, all sections equal size',
  },

  focus: {
    name: 'focus',
    label: 'Focus (15-70-15)',
    leftWidth: 15,
    middleWidth: 70,
    description: 'Main narrative emphasized, references on sides',
  },

  wideLeft: {
    name: 'wideLeft',
    label: 'Wide Left (40-30-30)',
    leftWidth: 40,
    middleWidth: 30,
    description: 'Left reference emphasized',
  },

  wideRight: {
    name: 'wideRight',
    label: 'Wide Right (30-30-40)',
    leftWidth: 30,
    middleWidth: 30,
    description: 'Right reference emphasized',
  },

  balanced: {
    name: 'balanced',
    label: 'Balanced (25-50-25)',
    leftWidth: 25,
    middleWidth: 50,
    description: 'Middle emphasized with equal references',
  },
};

// Width constraints
export interface SplitConstraints {
  /** Minimum width for left section (%) */
  leftMin: number;
  /** Maximum width for left section (%) */
  leftMax: number;
  /** Minimum width for middle section (%) */
  middleMin: number;
  /** Maximum width for middle section (%) */
  middleMax: number;
  /** Minimum width for right section (%) */
  rightMin: number;
}

export const SPLIT_CONSTRAINTS: SplitConstraints = {
  leftMin: 10,
  leftMax: 60,
  middleMin: 20, // Middle needs more space for narrative
  middleMax: 60,
  rightMin: 10,
};

// Default configuration
export const DEFAULT_SPLIT_CONFIG: SplitConfig = {
  enabled: false,
  leftWidth: 33,
  middleWidth: 34,
  leftFrameId: null,
  rightFrameId: null,
};

// localStorage key
export const SPLIT_CONFIG_STORAGE_KEY = 'sage3-split-config';
